import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { COLORS, BRAND } from '@/lib/brand';
import { Home, Package, ShoppingCart, History, User, LogOut, ClipboardCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const MobileLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/local', icon: Home, label: 'Inicio' },
    { path: '/local/order', icon: ShoppingCart, label: 'Pedido' },
    { path: '/local/history', icon: History, label: 'Historial' },
    ...(user?.lugares_recuento?.length ? [{ path: '/local/recuentos', icon: ClipboardCheck, label: 'Recuentos' }] : []),
    { path: '/local/profile', icon: User, label: 'Perfil' },
  ];

  return (
    <div 
      className="max-w-md mx-auto min-h-screen flex flex-col" 
      style={{ backgroundColor: COLORS.background.DEFAULT }}
    >
      {/* Header con fondo naranja TUPU Stock */}
      <header 
        className="px-4 py-3 sticky top-0 z-40 shadow-md"
        style={{ backgroundColor: COLORS.primary.DEFAULT }}
        data-testid="mobile-header"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <Package className="w-6 h-6" style={{ color: COLORS.primary.DEFAULT }} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{BRAND.name}</h1>
              <p className="text-xs text-white/80">{user?.name}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg transition-all text-white/80 hover:text-white"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 pb-20" data-testid="mobile-main-content">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav 
        className="fixed bottom-0 left-0 right-0 h-16 flex items-center justify-around z-50 max-w-md mx-auto"
        style={{ 
          backgroundColor: COLORS.background.surface, 
          borderTop: `1px solid ${COLORS.border.DEFAULT}` 
        }} 
        data-testid="mobile-bottom-nav"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === '/local/recuentos' ? location.pathname.startsWith(item.path) : location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center w-full h-full relative"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute top-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: COLORS.primary.DEFAULT }}
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
              <Icon 
                className="w-5 h-5" 
                style={{ color: isActive ? COLORS.primary.DEFAULT : COLORS.text.muted }} 
              />
              <span 
                className="text-xs mt-1" 
                style={{ 
                  color: isActive ? COLORS.primary.DEFAULT : COLORS.text.muted, 
                  fontWeight: isActive ? 500 : 400 
                }}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default MobileLayout;
